import React from "react";
import { Box } from "@mui/material";
import palette from "../../../common/styles/palette";
import fonts from "../../../common/styles/fonts";
import { cameraLabel, SEEK_VIDEO_EVENT, VideoSeekDetail } from "../../utils/videoSources";

// Timestamps do video-checklist-agent aparecem em dois formatos:
// - estruturado: { ts_start, ts_end } em `evidence[]`
// - embutido em prosa: "...sem adornos visíveis (00:00-00:05)." dentro de `criteria[].note`
// Este módulo é o único lugar que sabe converter "mm:ss" / "h:mm:ss" em segundos
// e pular o <video> da página para lá — a dica da IA no checklist (SkillItem)
// e os timestamps do relatório por eixo (ChecklistReportSection) consomem as
// funções abaixo em vez de duplicar o parsing.

const TIMESTAMP_RE = /\d{1,2}:\d{2}(?::\d{2})?(?:\s*[-–—]\s*\d{1,2}:\d{2}(?::\d{2})?)?/g;

export function tsToSeconds(ts: string): number | null {
  const m = /^\s*(\d+):(\d{2})(?::(\d{2}))?\s*$/.exec(String(ts || ""));
  if (!m) return null;
  return m[3] != null
    ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3])
    : Number(m[1]) * 60 + Number(m[2]);
}

// O player da rodada seleciona a fonte e usa o tempo local da evidência.
// Páginas legadas sem esse controle mantêm o salto no vídeo principal.
export function seekMainVideo(ts: string, sourceId?: string): void {
  const sec = tsToSeconds(ts);
  if (sec == null) return;
  const request = new CustomEvent<VideoSeekDetail>(SEEK_VIDEO_EVENT, {
    detail: { seconds: sec, sourceId }, cancelable: true,
  });
  if (!window.dispatchEvent(request)) {
    return;
  }
  // Uma evidência focal nunca deve cair no primeiro vídeo da página.
  if (sourceId && sourceId !== "camera1") return;
  const el = document.querySelector("video");
  if (el) {
    el.currentTime = sec;
    el.play?.()?.catch?.(() => {});
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  }
}

type TimestampChipProps = {
  /** Texto dentro dos colchetes (ex.: "00:08-00:11", "fala+video"). */
  label: string;
  /** Instante "mm:ss" para o seek. Ausente = pill apenas informativa. */
  ts?: string;
  sourceId?: string;
  onSeek?: (ts: string, sourceId?: string) => void;
};

/**
 * Pill de timestamp do mockup: monoespaçada, roxa, com o glifo ▶ quando é
 * clicável. Único lugar onde este estilo existe — evidências estruturadas,
 * timestamps em prosa e tags de origem do critério usam todos este componente.
 */
export function TimestampChip({
  label,
  ts,
  sourceId,
  onSeek = seekMainVideo,
}: TimestampChipProps) {
  const clickable = !!ts;
  return (
    <Box
      component="span"
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={ts ? () => onSeek(ts, sourceId) : undefined}
      onKeyDown={ts ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault(); onSeek(ts, sourceId);
        }
      } : undefined}
      title={ts ? `Ir para [${label}] ${sourceId ? `na ${cameraLabel(sourceId)}` : "no vídeo"}` : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        borderRadius: "6px",
        backgroundColor: palette.brand,
        color: palette.brandForeground,
        fontFamily: fonts.mono,
        fontSize: "0.75rem",
        fontWeight: 600,
        padding: "2px 6px",
        margin: "0 2px",
        verticalAlign: "baseline",
        cursor: clickable ? "pointer" : "default",
        "&:hover": clickable
          ? { backgroundColor: palette.brandDark }
          : undefined,
      }}
    >
      {clickable ? <span aria-hidden="true">▶</span> : null}
      {sourceId ? <span>{cameraLabel(sourceId)}</span> : null}
      <span>[{label}]</span>
    </Box>
  );
}

// Acha timestamps ("00:00", "00:00-00:05", "01:23:45–01:24:10") dentro de um
// texto livre e devolve o mesmo texto com cada timestamp virando uma pill
// clicável que pula o player para o início do trecho.
// Pontuação ao redor (ex.: parênteses) permanece como texto simples.
export function linkifyTimestamps(
  text: string | undefined | null,
  onSeek: (ts: string) => void = seekMainVideo
): React.ReactNode {
  if (!text) return text ?? null;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  const re = new RegExp(TIMESTAMP_RE);
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const full = match[0];
    const start = full.split(/[-–—]/)[0].trim();
    if (tsToSeconds(start) == null) continue;
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <TimestampChip
        key={`ts-${key++}`}
        label={full}
        ts={start}
        onSeek={onSeek}
      />
    );
    lastIndex = match.index + full.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}
